/**
 * Resolución de nombres geográficos de Guatemala (departamento / municipio) a partir de su
 * CÓDIGO numérico persistido. Única fuente para toda la app: reemplaza el `municipalities.find(
 * m => m.code === x)?.name` inline que estaba copypasteado en vistas, forms, documentos y la
 * generación del FEIC.
 *
 * Clave del asunto: los `code` de MUNICIPIO NO son únicos globalmente (8 colisionan entre
 * departamentos, p.ej. code 1202 = "San Pedro Sacatepéquez" de Guatemala Y de San Marcos), así
 * que el municipio se resuelve por `(department, code)`. El caller aporta el `department` (que
 * siempre vive junto al municipio en el mismo Place/Address). Los `code` de DEPARTAMENTO sí son
 * únicos.
 *
 * Devuelve el nombre TAL CUAL está en el catálogo (departamentos en MAYÚSCULA, municipios
 * capitalizados) o `undefined` si no resuelve — cada consumidor aplica su propio fallback y
 * casing (p.ej. el review del portal muestra "—" y uniforma a MAYÚSCULA).
 */
import { COUNTRY_LOCAL } from '@/shared/constants/config'
import departments from './departments-gt.json'
import municipalities from './municipalities-gt.json'

const DEPT_BY_CODE = new Map<number, string>(
  (departments as { code: number; name: string }[]).map((d) => [d.code, d.name])
)

const MUNI_BY_DEPT_CODE = new Map<string, string>()
const MUNI_BY_CODE = new Map<number, string>()
for (const m of municipalities as { code: number; department: number; name: string }[]) {
  MUNI_BY_DEPT_CODE.set(`${m.department}:${m.code}`, m.name)
  if (!MUNI_BY_CODE.has(m.code)) MUNI_BY_CODE.set(m.code, m.name)
}

/**
 * ¿Este lugar tiene departamento y municipio que valga la pena mostrar?
 *
 * Departamento y municipio son catálogos GUATEMALTECOS. Un lugar extranjero no los tiene, pero el
 * dato guardado sí puede traerlos: durante un tiempo el formulario del representante legal jurídico
 * dejaba pegado el default de Guatemala al elegir otro país de nacimiento, y todo lo que mostraba
 * ese expediente —vistas, paneles y el FEIC impreso— preguntaba si el dato EXISTÍA en vez de de qué
 * país era. El FEIC que se firmó decía «País: COLOMBIA · Departamento: GUATEMALA · Municipio:
 * GUATEMALA».
 *
 * La regla es la MISMA que el JSON de la IVE ya aplicaba en `transformPlace`, que por eso nunca
 * salió mal: si hay país declarado y no es Guatemala, no hay detalle local. Un lugar SIN país se
 * muestra igual —los expedientes viejos guatemaltecos no siempre lo tienen, y esconderles el
 * departamento sería perder un dato bueno por culpa de uno malo.
 *
 * Arreglar el formulario no alcanzaba: limpia lo que se capture de ahora en más, no lo que ya está
 * guardado. Esto es lo que hace que un expediente viejo se vea bien sin tocar la base.
 */
export function hasLocalPlaceDetail(
  place: { country?: string | null; department?: number | string | null } | null | undefined
): boolean {
  if (place?.department == null || place.department === '') return false
  return !place?.country || place.country === COUNTRY_LOCAL.isoCode
}

/** Nombre del departamento por código (o `undefined`). Los códigos de departamento son únicos. */
export function departmentName(code: unknown): string | undefined {
  if (code == null || code === '') return undefined
  return DEPT_BY_CODE.get(Number(code))
}

/**
 * Nombre del municipio por código. `department` desambigua los códigos que colisionan; sin él
 * cae a la primera coincidencia por code. Devuelve `undefined` si no resuelve.
 */
export function municipalityName(code: unknown, department?: unknown): string | undefined {
  if (code == null || code === '') return undefined
  const c = Number(code)
  const d = department == null || department === '' ? undefined : Number(department)
  return (d != null ? MUNI_BY_DEPT_CODE.get(`${d}:${c}`) : undefined) ?? MUNI_BY_CODE.get(c)
}

/**
 * Los nombres de departamento y municipio listos para mostrar, VACÍOS si el lugar es extranjero.
 *
 * Existe para que los documentos no repitan el guard en cada bloque: los generadores del FEIC y de
 * los anexos arman una y otra vez el mismo trío `{ país, departamento, municipio }`, y cada copia
 * era una oportunidad más de olvidarse de mirar el país. El país lo sigue resolviendo cada
 * consumidor, que lo saca de catálogos distintos.
 *
 *     birthplace: { country: nombreDelPais, ...localPlaceNames(representative.birthplace) }
 */
export function localPlaceNames(
  place:
    | {
        country?: string | null
        department?: number | string | null
        municipality?: number | string | null
      }
    | null
    | undefined
): { department: string; municipality: string } {
  if (!hasLocalPlaceDetail(place)) return { department: '', municipality: '' }
  return {
    department: departmentName(place?.department) ?? '',
    municipality: municipalityName(place?.municipality, place?.department) ?? ''
  }
}
